import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { MapSelectionStreamService } from 'src/app/feature/map-viewer/map-selection/selection-stream.service';

import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { GspTextType } from 'src/app/shared/common/components/gsp-text/gsp-text.component';
import { TaskSelectionCriteria } from 'src/app/shared/common/current-features/selection-criteria';
import { TasksStore } from 'src/app/shared/common/current-tasks/tasks-store.service';
import { TasksStreamsService } from 'src/app/shared/common/current-tasks/tasks-streams.service';
import { FormStatus, Task, TaskFeature } from 'src/app/shared/common/task/task';
import { Actor } from 'src/app/shared/user/actor';

@Component({
    selector: 'task-assigned-users',
    templateUrl: './task-assigned-users.component.html'
})
export class TaskAssignedUsersComponent implements OnInit {
    @Input()
    task: Task;

    @Input()
    isTaskEditMode: boolean = false;

    @Input()
    modifyPermission: boolean = false;

    @Output()
    closeAssignedUsersPanel = new EventEmitter<void>();

    @Output()
    selectedAssigneesChange = new EventEmitter<Partial<Actor>[]>();

    public assignedUsers: Partial<Actor>[];
    public newAssignedUsers: Partial<Actor>[] = [];
    public taskFeatures: TaskFeature[];
    public currentProjectAssignees: Actor[];

    public loading = false;

    public assigneeTaskFeaturesMap: {
        [assigneeId: string]: {
            newFeatures: TaskFeature[];
            updatedFeatures: TaskFeature[];
        };
    } = {};
    public userFilter: string;
    public isAddUserMode: boolean = false;

    // Expose enum to template
    public ButtonType = ButtonType;
    public GspTextType = GspTextType;

    constructor(
        private selectionStream: MapSelectionStreamService,
        private tasksStore: TasksStore,
        private tasksStreams: TasksStreamsService,
        private messaging: MessagingService,
        private translate: TranslationService
    ) {}

    public ngOnInit(): void {
        this.taskFeatures = this.task.features;
        this.currentProjectAssignees = this.tasksStore.getAllAssignees();
        this.assignedUsers = this.currentProjectAssignees
            .filter(assignee => this.task.assignees.some(taskAssignee => taskAssignee.id === assignee.id))
            .map(assignee => {
                const taskAssignee = this.task.assignees.find(taskAssignee => taskAssignee.id === assignee.id);
                return { ...assignee, isTodoCompleted: taskAssignee.isTodoCompleted };
            });

        this.createAssigneeToTaskFeaturesMap();
    }

    private createAssigneeToTaskFeaturesMap() {
        this.assignedUsers.forEach(assignee => {
            const { newFeatures, updatedFeatures } = this.taskFeatures.reduce(
                (acc, taskFeature) => {
                    // Feature corresponding to the TaskFeature
                    const matchedFeature = this.task.taskFeatures?.find(
                        feature => feature.id === taskFeature.featureId
                    );

                    if (
                        taskFeature.status === FormStatus.NEW &&
                        matchedFeature?.metadata.collection_collectedBy === assignee.id
                    ) {
                        acc.newFeatures.push(taskFeature);
                    } else if (
                        taskFeature.status === FormStatus.UPDATED &&
                        matchedFeature?.metadata.collection_updatedBy === assignee.id
                    ) {
                        acc.updatedFeatures.push(taskFeature);
                    }

                    return acc;
                },
                { newFeatures: [], updatedFeatures: [] }
            );

            this.assigneeTaskFeaturesMap[assignee.id] = {
                newFeatures,
                updatedFeatures
            };
        });

        this.selectedAssigneesChange.emit(this.assignedUsers);
    }

    public removeUserFromSelection(userId: string) {
        if (this.isAddUserMode) {
            this.newAssignedUsers = this.newAssignedUsers.filter(user => user.id !== userId);
            return;
        }
        this.assignedUsers = this.assignedUsers.filter(user => user.id !== userId);
        this.task.assignees = this.assignedUsers;

        this.createAssigneeToTaskFeaturesMap();
    }

    public getUsersNotAssigned(): Actor[] {
        return this.currentProjectAssignees.filter(
            user => !this.assignedUsers.some(assignedUser => assignedUser.id === user.id)
        );
    }

    public hasUpdatedOrNewForms(assigneeId: string): boolean {
        return !!(
            this.assigneeTaskFeaturesMap[assigneeId]?.newFeatures.length ||
            this.assigneeTaskFeaturesMap[assigneeId]?.updatedFeatures.length
        );
    }

    public selectAssigneeForms(assigneeId: string) {
        if (this.assigneeTaskFeaturesMap[assigneeId]) {
            const userUpdatedFeatures = [
                ...this.assigneeTaskFeaturesMap[assigneeId].newFeatures.map(feature => feature.featureId),
                ...this.assigneeTaskFeaturesMap[assigneeId].updatedFeatures.map(feature => feature.featureId)
            ];

            this.tasksStreams.disableMapviewerStream.next(false);
            this.selectionStream.stream.next(new TaskSelectionCriteria([this.task.id], userUpdatedFeatures));
        }
    }

    public async saveAssignedUsers() {
        this.loading = true;

        try {
            const newAssignedUserIds = this.newAssignedUsers.map(newUser => newUser.id);
            const newAssignees = this.currentProjectAssignees.filter(user => newAssignedUserIds.includes(user.id));
            this.assignedUsers = this.assignedUsers.concat(newAssignees);
            this.task.assignees = this.assignedUsers;

            await this.tasksStore.saveTask(this.task);

            this.createAssigneeToTaskFeaturesMap();
            this.messaging.showSuccess(this.translate.instant('TC.Todos.Message.Saved'));
        } catch (error) {
            this.messaging.showError(this.translate.instant('TC.Common.ErrorWhileUpdating'));
        } finally {
            this.loading = false;
            this.back();
        }
    }

    public back() {
        if (this.isAddUserMode) {
            this.isAddUserMode = false;
            this.newAssignedUsers = [];
            return;
        }
        this.tasksStreams.disableMapviewerStream.next(true);
        this.closeAssignedUsersPanel.emit();
    }
}
